import type { ReactNode, JSX } from 'react'
import { useMemo, useState } from 'react'

import { Command } from 'cmdk'

import {
  EmptyState,
  AllChannelsIcon,
  Button,
  Checkbox,
  PlusIcon,
  Text,
} from '@buffer-mono/popcorn'

import {
  ChannelSelector,
  mapGraphQLChannelToNormalized,
  useChannelGroupsFilter,
} from '~publish/components/ChannelSelector'
import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf, ResultOf } from '~publish/graphql'
import { useChannelAgnosticDraftsEnabled } from '~publish/hooks/useChannelAgnosticDraftsEnabled'

import { OpenChannelStoreFrontTrigger } from '../OpenChannelStoreFrontTrigger'

import { FilterByChannelView } from './FilterByChannelView'
import styles from './FilterByChannel.module.css'

/**
 * Adds channel-less content items to the selection, combinable with a real
 * channel selection (both narrow the drafts list; the fetch layer folds them
 * into one query). Whether it's also combinable with a tag filter is each
 * consumer's own call — List view's Drafts tab treats them as mutually
 * exclusive; Board's Draft column combines both.
 */
export const NO_CHANNELS_VALUE = 'no-channels'

/** Strips the sentinel so real-channel consumers (queries, composer prefill) never see it. */
export function splitNoChannelsSelection(value: string[]): {
  isNoChannelsSelected: boolean
  realChannelIds: string[]
} {
  return {
    isNoChannelsSelected: value.includes(NO_CHANNELS_VALUE),
    realChannelIds: value.filter((id) => id !== NO_CHANNELS_VALUE),
  }
}

export const FilterByChannel_Channel = graphql(/* GraphQL */ `
  fragment FilterByChannel_Channel on Channel {
    id
    type
    name
    service
    products
    allowedActions
    avatar
    serverUrl
    timezone
    locationData {
      location
    }
  }
`)

/**
 * Masked type provided by parent query result.
 * Use `useFilterByChannelFragment` to get the unmasked type.
 */
export type MaskedFilterByChannelFragment = readonly FragmentOf<
  typeof FilterByChannel_Channel
>[]

/**
 * Unmasked type extracted from parent query result.
 */
export type FilterByChannelFragment = ResultOf<typeof FilterByChannel_Channel>

/**
 * List of unmasked channel filter fragments extracted from parent query result.
 */
export type FilterByChannelFragments = readonly ResultOf<
  typeof FilterByChannel_Channel
>[]

/**
 * Unmask channel filters from the parent query result into typed
 *  fragments.
 * @param channels masked channel filter fragments from the parent query result
 * @returns
 */
export const useFilterByChannelFragment = (
  channels: MaskedFilterByChannelFragment,
): FilterByChannelFragments => readFragment(FilterByChannel_Channel, channels)

type FilterByChannelProps = {
  channels: MaskedFilterByChannelFragment
  /**
   * Selected channel ids
   */
  value: string[]
  onSelect: (channelIds: string[]) => void
  className?: string
  /**
   * Optional set of channel IDs that should be displayed as disabled.
   * Disabled channels can still be unselected if already selected.
   */
  disabledChannelIds?: Set<string>
  /**
   * Optional per-channel explanation shown in a tooltip on disabled rows (why
   * that channel can't be selected). Only consulted for ids in
   * {@link disabledChannelIds}.
   */
  getDisabledChannelReason?: (channelId: string) => ReactNode
  /** Channel IDs with view-only access — rows stay selectable but show an eye icon. */
  viewOnlyChannelIds?: Set<string>
  /** Tooltip shown on view-only rows; the copy names the product, so each surface supplies its own. */
  viewOnlyChannelReason?: ReactNode
  /**
   * Shows the "No channels" row, combinable with a real channel selection.
   * Only surfaces with channel-less content to filter to — List view's
   * Drafts tab, Board's Draft column — set this; others default to false.
   */
  showNoChannelsOption?: boolean
}

/**
 * Component to enable filtering by channels, using ChannelSelector
 * It exposes fragment to fetch channels, tags array and selected tags
 */
export const FilterByChannel = ({
  channels: passedChannels,
  value,
  onSelect,
  className,
  disabledChannelIds,
  getDisabledChannelReason,
  viewOnlyChannelIds,
  viewOnlyChannelReason,
  showNoChannelsOption = false,
}: FilterByChannelProps): JSX.Element => {
  const channels = readFragment(FilterByChannel_Channel, passedChannels)
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const channelAgnosticDraftsEnabled = useChannelAgnosticDraftsEnabled()
  const [open, setOpen] = useState(false)

  const { isNoChannelsSelected, realChannelIds: selectedRealChannelIds } =
    splitNoChannelsSelection(value)

  // Re-appends the sentinel after a real-channel-only change, so picking
  // channels alongside "No channels" doesn't drop it (@see NO_CHANNELS_VALUE).
  const onSelectRealChannels = (channelIds: string[]): void => {
    onSelect(
      isNoChannelsSelected ? [...channelIds, NO_CHANNELS_VALUE] : channelIds,
    )
  }

  // Real-channel-only, so a group toggle can never mix the sentinel into the
  // resulting selection (@see NO_CHANNELS_VALUE).
  const {
    filteredChannelGroups,
    selectedChannelGroupIds,
    disabledChannelGroupIds,
    handleSelectGroup,
    isExpanded: isChannelGroupsExpanded,
    setIsExpanded: setIsChannelGroupsExpanded,
  } = useChannelGroupsFilter({
    selectedChannelIds: selectedRealChannelIds,
    onChannelSelect: onSelectRealChannels,
    open,
    disabledChannelIds,
  })

  const normalizedChannels = useMemo(
    () => channels.map(mapGraphQLChannelToNormalized),
    [channels],
  )

  const toggleNoChannels = (): void => {
    onSelect(
      isNoChannelsSelected
        ? selectedRealChannelIds
        : [...selectedRealChannelIds, NO_CHANNELS_VALUE],
    )
  }

  const handleSelectAll = (): void => {
    if (selectedRealChannelIds.length > 0) {
      onSelectRealChannels([])
    } else {
      // Never select disabled channels (e.g. ones the actor can't access) —
      // "Select all" must respect the same gating as the individual rows.
      onSelectRealChannels(
        channels
          .filter((channel) => !disabledChannelIds?.has(channel.id))
          .map((channel) => channel.id),
      )
    }
  }

  const emptyStateAdmin = (
    <EmptyState size="small" variant="primary">
      <EmptyState.Icon>
        <AllChannelsIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>No channels found</EmptyState.Heading>
      <EmptyState.Description>
        <OpenChannelStoreFrontTrigger cta="publish-filterByChannel-emptyState-connectAChannel-1">
          <Button>
            <PlusIcon /> Connect a Channel
          </Button>
        </OpenChannelStoreFrontTrigger>
      </EmptyState.Description>
    </EmptyState>
  )

  const emptyStateMember = (
    <EmptyState size="small" variant="primary">
      <EmptyState.Heading>Filter posts by channel</EmptyState.Heading>
      <EmptyState.Description>
        Contact an account administrator to access to your team&apos;s channels.
      </EmptyState.Description>
    </EmptyState>
  )

  const emptyStateContent =
    channels.length === 0
      ? canManageChannels
        ? emptyStateAdmin
        : emptyStateMember
      : undefined

  return (
    <FilterByChannelView
      channels={normalizedChannels}
      value={selectedRealChannelIds}
      onChange={onSelectRealChannels}
      onSelectAll={handleSelectAll}
      count={value.length}
      open={open}
      onOpenChange={setOpen}
      className={className}
      groups={
        filteredChannelGroups.length > 0 && (
          <ChannelSelector.GroupsOverflow
            groups={filteredChannelGroups}
            selectedIds={selectedChannelGroupIds}
            disabledIds={disabledChannelGroupIds}
            onSelectGroup={handleSelectGroup}
            isExpanded={isChannelGroupsExpanded}
            onExpand={() => setIsChannelGroupsExpanded(true)}
          >
            <ChannelSelector.NewGroupLink>
              <PlusIcon size={16} />
              Group
            </ChannelSelector.NewGroupLink>
          </ChannelSelector.GroupsOverflow>
        )
      }
      emptyState={emptyStateContent}
      getItemProps={(channel) => {
        const isDisabled = disabledChannelIds?.has(channel.id) ?? false
        return {
          disabled: isDisabled,
          disabledReason: isDisabled
            ? getDisabledChannelReason?.(channel.id)
            : undefined,
          viewOnly: viewOnlyChannelIds?.has(channel.id),
          viewOnlyReason: viewOnlyChannelReason,
        }
      }}
      footer={
        channelAgnosticDraftsEnabled &&
        showNoChannelsOption && (
          <>
            <Command.Separator className={styles.separator} />
            <Command.Item
              value="No channels"
              onSelect={toggleNoChannels}
              className={styles.noChannelsItem}
            >
              <Text weight="medium" className={styles.noChannelsLabel}>
                No channels
              </Text>
              <Checkbox
                checked={isNoChannelsSelected}
                tabIndex={-1}
                className={styles.noChannelsCheckbox}
              />
            </Command.Item>
          </>
        )
      }
    />
  )
}
